import { styled } from '@mui/material/styles';
import { Box, IconButton } from '@mui/material';

const TOYOTA_RED = '#EB0A1E';

export const PageShell = styled(Box)(() => `
  background: #f5f6f8;
  min-height: calc(100vh - 64px);
  padding: 32px 24px 64px;
`);

export const ContentWrapper = styled(Box)(() => `
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 56px 56px;
  background: #ffffff;
  border: 1px solid #e6e8eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06), 0 1px 2px rgba(16, 24, 40, 0.04);

  @media (max-width: 600px) {
    padding: 24px 20px 40px;
  }
`);

export const ContentTitle = styled('h1')(() => `
  font-family: 'Toyota', sans-serif;
  font-weight: 700;
  font-size: 30px;
  line-height: 1.25;
  color: #101828;
  margin: 0 0 12px 0;
  text-align: center;
`);

export const MetaRow = styled(Box)(() => `
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding-bottom: 20px;
  margin-bottom: 28px;
  border-bottom: 1px solid #eceef1;
`);

export const StatusChip = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone?: 'ok' | 'warn' | 'muted' }>(({ tone = 'muted' }) => {
  const palette = {
    ok: { bg: '#ECFDF3', fg: '#067647', bd: '#ABEFC6' },
    warn: { bg: '#FFFAEB', fg: '#B54708', bd: '#FEDF89' },
    muted: { bg: '#F2F4F7', fg: '#475467', bd: '#E4E7EC' },
  }[tone];
  return `
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-transform: capitalize;
    padding: 3px 10px;
    border-radius: 999px;
    background: ${palette.bg};
    color: ${palette.fg};
    border: 1px solid ${palette.bd};
  `;
});

export const ContentWarning = styled('div')(() => `
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #FFF5F5;
  border: 1px solid ${TOYOTA_RED};
  color: #B42318;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 24px;
`);

export const ContentBody = styled('div')(() => `
  font-family: 'Toyota', sans-serif;
  font-size: 15px;
  color: #344054;
  line-height: 1.7;
  word-wrap: break-word;

  h1, h2, h3, h4, h5, h6 {
    color: #101828;
    font-weight: 700;
    line-height: 1.3;
    margin: 32px 0 12px;
    scroll-margin-top: 160px;
  }
  h1 { font-size: 24px; }
  h2 { font-size: 20px; border-bottom: 1px solid #eceef1; padding-bottom: 6px; }
  h3 { font-size: 17px; }
  h4 { font-size: 15px; }

  p { margin: 0 0 16px; }

  a {
    color: ${TOYOTA_RED};
    text-decoration: none;
    border-bottom: 1px solid rgba(235, 10, 30, 0.3);
    transition: border-color 0.15s ease;
  }
  a:hover { border-bottom-color: ${TOYOTA_RED}; }

  ul, ol { margin: 0 0 16px; padding-left: 24px; }
  li { margin: 4px 0; }

  blockquote {
    margin: 16px 0;
    padding: 8px 16px;
    border-left: 4px solid ${TOYOTA_RED};
    background: #fafbfc;
    color: #475467;
    font-style: italic;
  }

  code {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.86em;
    background: #f2f4f7;
    color: #b42318;
    padding: 2px 6px;
    border-radius: 4px;
  }

  hr {
    border: none;
    border-top: 1px solid #eceef1;
    margin: 28px 0;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 16px auto;
    border-radius: 6px;
  }
  img.kb-image-align-left { margin-left: 0; margin-right: auto; }
  img.kb-image-align-right { margin-left: auto; margin-right: 0; }

  .kb-toc {
    margin: 0 0 24px;
    padding: 16px 20px;
    border: 1px solid #e4e7ec;
    border-radius: 6px;
    background: #f9fafb;
  }
  .kb-toc ul { margin: 0; }

  .kb-layout-section {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }
  .kb-layout-cell { min-width: 0; }
  .kb-layout-two-equal { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .kb-layout-two-left-sidebar { grid-template-columns: minmax(0, 1fr) minmax(0, 2fr); }
  .kb-layout-two-right-sidebar { grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); }
  .kb-layout-three-equal { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .kb-layout-three-with-sidebars { grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr); }
  @media (max-width: 720px) {
    .kb-layout-section { grid-template-columns: minmax(0, 1fr); }
  }

  .kb-embed-fallback {
    margin: 16px 0;
    padding: 12px 16px;
    border: 1px solid #e4e7ec;
    border-radius: 6px;
    background: #f9fafb;
  }
  .kb-embed-fallback p { margin: 8px 0 0; }

  video, audio {
    display: block;
    max-width: 100%;
    margin: 16px auto;
  }
  video {
    width: min(100%, 960px);
    border-radius: 6px;
    background: #101828;
  }
  audio { width: min(100%, 640px); }

  .kb-embed {
    position: relative;
    width: 100%;
    min-height: 360px;
    margin: 16px 0;
  }
  .kb-embed iframe {
    width: 100%;
    min-height: 480px;
    border: 1px solid #e4e7ec;
    border-radius: 6px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: 14px;
    border-radius: 8px;
    border: 1px solid #e4e7ec;
  }
  td, th {
    border-bottom: 1px solid #e4e7ec;
    padding: 10px 12px;
    text-align: left;
  }
  th {
    background-color: #f9fafb;
    font-weight: 600;
    color: #344054;
  }
  tr:last-child td { border-bottom: none; }
  tbody tr:nth-of-type(even) { background: #fcfcfd; }
  .kb-table-scroll {
    max-width: 100%;
    overflow-x: auto;
  }

  .kb-panel {
    margin: 16px 0;
    padding: 14px 16px;
    border: 1px solid #b2ddff;
    border-left: 4px solid #1570ef;
    border-radius: 6px;
    background: #eff8ff;
  }
  .kb-panel-warning, .kb-panel-error {
    border-color: #fecdca;
    border-left-color: #d92d20;
    background: #fef3f2;
  }
  .kb-panel-tip, .kb-panel-success {
    border-color: #abefc6;
    border-left-color: #079455;
    background: #ecfdf3;
  }

  .kb-expand {
    margin: 16px 0;
    border: 1px solid #e4e7ec;
    border-radius: 6px;
  }
  .kb-expand summary {
    padding: 12px 16px;
    color: #101828;
    font-weight: 600;
    cursor: pointer;
    background: #f9fafb;
  }
  .kb-expand > div { padding: 16px; }

  .kb-unsupported-macro, .kb-missing-attachment {
    display: inline-block;
    margin: 8px 0;
    padding: 8px 10px;
    border: 1px dashed #f79009;
    border-radius: 4px;
    background: #fffaeb;
    color: #93370d;
    font-size: 13px;
  }

  .kb-image-loading, .kb-media-loading {
    display: block;
    min-height: 80px;
    margin: 16px 0;
    border-radius: 6px;
    background: #f2f4f7;
  }

  a[aria-disabled='true'] {
    color: #667085;
    cursor: wait;
    pointer-events: none;
  }

  pre { overflow: auto; }
`);

export const CodeBlockWrapper = styled(Box)(() => `
  position: relative;
  margin: 20px 0;

  & pre {
    margin: 0 !important;
    border-radius: 8px !important;
    font-size: 13px !important;
  }
`);

export const CopyButton = styled(IconButton)(() => `
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 6px;
  color: #cfd4dc;
  background: rgba(255, 255, 255, 0.08);

  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.18);
  }
`);

export const LoadingWrapper = styled(Box)(() => `
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  width: 100%;
  gap: 16px;
  color: #616161;
  font-size: 14px;
`);

export const ErrorWrapper = styled(Box)(() => `
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  width: 100%;
  gap: 12px;
  text-align: center;
  color: #475467;
`);

export const BackToTop = styled(IconButton)(() => `
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: 1200;
  background: ${TOYOTA_RED};
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(235, 10, 30, 0.35);

  &:hover {
    background: #c50818;
  }
`);
